import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { api, ApiError } from "../api";
import { forgetPendingClaim, peekPendingClaim } from "../claim";
import { AuthError, AuthField, AuthShell } from "../components/AuthShell";

/** A new customer's first sign-in: the box label proves the purchase, and
 *  the account, its first location and the box placement are made together. */
export default function Signup() {
  const nav = useNavigate();
  const pending = peekPendingClaim();
  const [form, setForm] = useState({
    serial: pending?.serial ?? "",
    claim_code: pending?.claim_code ?? "",
    business_name: "",
    location_name: "",
    username: "",
    email: "",
    password: "",
    confirm: "",
  });
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [k]: e.target.value });

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (form.password !== form.confirm) {
      setErr("The two passwords do not match.");
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      await api.signupWithBox({
        serial: form.serial.trim(),
        claim_code: form.claim_code.trim(),
        business_name: form.business_name.trim(),
        location_name: form.location_name.trim(),
        username: form.username.trim(),
        password: form.password,
        ...(form.email.trim() ? { email: form.email.trim() } : {}),
      });
      forgetPendingClaim();
      nav({ to: "/settings", search: { section: "connectors" } });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Could not create the account.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell
      wide
      title="Create your account"
      subtitle="Use the serial number and claim code on your IntraScene box."
      footer={
        <>
        <p>
          Already have an account?{" "}
          <Link to="/login" className="font-medium text-brand-fuchsia hover:underline">
            Sign in
          </Link>{" "}
          and add the box from Settings.
        </p>
        <p>
          Box not switched on yet?{" "}
          <Link to="/box" className="font-medium text-brand-fuchsia hover:underline">
            Check its connection
          </Link>
        </p>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <AuthField
            label="Serial number"
            value={form.serial}
            onChange={set("serial")}
            placeholder="ISC-2610-0001"
            autoComplete="off"
            required
          />
          <AuthField
            label="Claim code"
            value={form.claim_code}
            onChange={set("claim_code")}
            placeholder="ABCD-EFGH"
            autoComplete="off"
            required
          />
        </div>
        <AuthField
          label="Business name"
          value={form.business_name}
          onChange={set("business_name")}
          autoComplete="organization"
          required
        />
        <AuthField
          label="Where is this box?"
          hint="The name of the location, for example Main Street."
          value={form.location_name}
          onChange={set("location_name")}
          required
        />
        <div className="grid grid-cols-2 gap-3">
          <AuthField
            label="Username"
            value={form.username}
            onChange={set("username")}
            pattern="[A-Za-z0-9._@+\-]{2,64}"
            title="Letters, numbers and . _ @ + -"
            autoComplete="username"
            required
          />
          <AuthField
            label="Email"
            type="email"
            value={form.email}
            onChange={set("email")}
            autoComplete="email"
            hint="For password resets."
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <AuthField
            label="Password"
            type="password"
            value={form.password}
            onChange={set("password")}
            minLength={10}
            autoComplete="new-password"
            hint="At least 10 characters."
            required
          />
          <AuthField
            label="Repeat password"
            type="password"
            value={form.confirm}
            onChange={set("confirm")}
            minLength={10}
            autoComplete="new-password"
            required
          />
        </div>
        <AuthError message={err} />
        <button className="bf-button w-full py-2.5" disabled={loading} type="submit">
          {loading ? "Creating your account..." : "Create account"}
        </button>
      </form>
    </AuthShell>
  );
}
